
<template>
  <div id="app">
    <header class="app-header">
      <div class="inner-header">
        <a href="https://app.academiclabs.com" class="navbar-brand">
          <icon icon="a-white" :width="15" :height="15" class="al-logo" />
          <span class="al-logo-text">AcademicLabs</span>
        </a>
      </div>
    </header>
    <section class="app-body">
      <template v-if="error">
        <div class="t-500">
          Something went wrong, contact us via <a href="https://academiclabs.com" target="_blank">AcademicLabs</a> or try refreshing the page.
        </div>
      </template>
      <template v-else-if="counts.length === 0 && isLoading">
        <div style="height: 150px; width: 100%;" class="d-flex align-items-center justify-content-center">
          <span class="pt-2 v-spinner">
            <div class="v-pulse v-pulse1">
            </div><div class="v-pulse v-pulse2">
            </div><div class="v-pulse v-pulse3">
            </div>
          </span>
        </div>
      </template>
      <template v-else>
        <div class="inner-body">
          <div class="inner-left">
            <h2 class="t-primary">Search & Discover</h2>
          </div>
          <div class="inner-right">
            <form
              @submit.prevent="fetchCounts"
              class="mb-4 search_bar_form d-flex flex-column"
            >
              <span class="search_bar_input flex-grow-1 d-flex justify-content-between align-items-stretch">
                <input
                  type="text"
                  class="input__field form-control bg-white"
                  placeholder="Search for e.g. Charcot-Marie-Tooth, target proteine degradation, AAV gene therapy"
                  v-model="keyword"
                />
                <button
                  class="btn search-button-header-color"
                ><i class="fa fa-search pr-2" /><span class="t-white">Search</span></button>
              </span>
            </form>
            <div class="row">
              <div v-for="category in counts" :key="category.name" class="col">
                <div
                  class="card mb-2 t-primary"
                  :class="{
                    'is-disabled': category.total <= 0 && !isLoading,
                    'is-loading': isLoading
                  }"
                >
                  <div class="d-flex align-items-center">
                    <i :class="icons[category.index]" class="t-secondary mr-2 t-xlr" style="width: 20px" />
                    <span class="t-500 t-xxlr category-title">{{ category.name }}</span>
                  </div>
                  <div class="d-flex align-items-center mt-1" style="height: 24px;line-height: 2rem;">
                    <span class="mr-2" style="width: 20px;" />
                    <div v-if="isLoading">
                      <span class="pt-2 v-spinner">
                        <div class="v-pulse v-pulse1">
                        </div><div class="v-pulse v-pulse2">
                        </div><div class="v-pulse v-pulse3">
                        </div>
                      </span>
                    </div>
                    <span v-else class="t-500 t-xxlr"
                      :class="{
                        't-blue': category.total > 0,
                        't-secondary': category.total === 0
                      }"
                    >{{ category.total }}</span>
                  </div>
                  <div v-if="!isLoading && category.total > 0" class="mt-1 pl-2" style="margin-left: 20px;">
                    <span
                        v-for="(result, i) in category.results"
                        :key="`${category.index}-${i}`"
                        class="d-flex align-items-center"
                    >
                      <i :class="icons[category.index]" class="t-secondary mr-2" />
                      <a
                        :href="getUrl(category.url, category.index, result.slug_values, result.slug_text)" target="_blank"
                        :title="result.name"
                        class="t-overflow"
                      >
                        {{ result.name}}
                      </a>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import Icon from '@/components/shared/Icon'

import { ICONS } from '@/assets/js/_data'

export default {
  name: 'embedApp',
  components: {
    Icon
  },
  data () {
    return {
      apiKey: null,
      keyword: '',
      isLoading: false,
      counts: [],
      icons: ICONS,
      error: false
    }
  },
  mounted () {
    const queryString = window.location.search
    // Further parsing:
    const params = new URLSearchParams(queryString)
    const q = params.get('api_key')
    this.apiKey = q

    // const keyword = params.get('keyword')
    // this.keyword = keyword
    this.fetchCounts()
  },
  methods: {
    getUrl (baseUrl, index, slugValues, slugText) {
      if (index === 'researchers' || index === 'research_groups' || index === 'organisations') {
        switch (index) {
          case 'researchers': return `${process.env.VUE_APP_FRONT_URL}profile/${slugValues[0]}`
          case 'research_groups': return `${process.env.VUE_APP_FRONT_URL}research_group/${slugValues[0]}`
          case 'organisations': return `${process.env.VUE_APP_FRONT_URL}organisation/${slugValues[0]}`
        }
      } else {
        return `${baseUrl}${this.keyword ? '&' : '?'}${this.getIndexForSearchModal(index)}=${slugValues[0]}&hr=${slugText}`
      }
    },
    getIndexForSearchModal (index) {
      switch (index) {
        case 'research_topics': return 'research_topic'
        case 'projects': return 'project'
        case 'clinical_trials': return 'clinical_trial'
        case 'patents': return 'patent'
        case 'publications': return 'publication'
      }
    },
    getIndexAsString (index) {
      switch (index) {
        case 'research_groups': return 'Research Groups'
        case 'research_topics': return 'Ongoing Research'
        case 'organisations': return 'Companies'
        case 'projects': return 'Funded Projects'
        case 'clinical_trials': return 'Clinical Trials'
        case 'patents': return 'Patents'
        case 'publications': return 'Publications'
        case 'researchers': return 'Researchers'
      }
    },
    async fetchCounts (retries = 0) {
      if (this.isLoading) this.controller.abort()

      if (retries >= 5) {
        this.error = true
        this.isLoading = false
        return
      }
      this.error = false
      this.isLoading = true
      try {
        this.controller = new AbortController()

        const data = await axios.post(`${process.env.VUE_APP_SEARCH_URL}/count_embedded`,
          {
            keyword: this.keyword,
            api_key: this.apiKey
          },
          { signal: this.controller.signal }
        )
        const counts = Object.entries(data.data.categories).map(([key, value]) => {
          return {
            index: key,
            name: this.getIndexAsString(key),
            ...value
          }
        })
        if (counts.length > 0) {
          counts.sort((a, b) => a.order - b.order)
          this.counts = counts
        }
        this.isLoading = false
      } catch (e) {
        if (e.message === 'canceled') {
          // Cancelled request because we started a new one, all good
          return
        }

        if (retries < 5) {
          this.fetchCounts(retries + 1)
        } else {
          this.error = true
          this.isLoading = false
          // Something else went wrong...
        }
      }
    }
  }
}
</script>
